import { Injectable } from "@angular/core";
import { ValuesService } from "../../../../common/values/values.service";
import { DeviceStatusIcons, EventDescriptors, EventTypes } from "./events.model";

export interface MultipleConditionalTextElement {
    text: string;
    placeholders: any;
}

export interface DeviceStatusInfo {
    text: string;
    icon: DeviceStatusIcons;
}

@Injectable({
    providedIn: 'root'
})

export class EventsService {

    constructor(
        private valuesService: ValuesService
    ) {}

    /**
     * Gets the object with the text and icon for the device status the event is referring to
     * @param dictionary The dictionary that contains the text keys
     * @param eventProperties The event properties that help to determine the text key
     * @returns {DeviceStatusInfo} The device status info
     */
    public getEventDeviceStatusInfo(dictionary, eventProperties?): DeviceStatusInfo {
        let property = dictionary[EventDescriptors.DEVICE_STATUS];
        if (property && property.type && property.type === EventTypes.CONDITIONAL) {
            property = this.getEventPropertyTexts(EventDescriptors.DEVICE_STATUS, eventProperties, dictionary);
        }

        return {
            text: property?.text ?? '',
            icon: property?.icon ?? DeviceStatusIcons.ZERO
        };
    }

    /**
     * Based on a given dictionary(object) and some given properties of an event, returns a string/strings/MultipleConditionalTextElement(s)
     * coresponding to a the given property
     * @param dictionaryProperty The property that needs a result from the dictionary
     * @param eventProperties The event properties that help to determine the text key
     * @param dictionary The dictionary that contains the text keys
     * @returns {string|Array<string>|MultipleConditionalTextElement[]} A string or strings or MultipleConditionalTextElement(s)
     */
    public getEventPropertyTexts(dictionaryProperty, eventProperties, dictionary): string|Array<string>|MultipleConditionalTextElement[] {
        if (!dictionary?.hasOwnProperty(dictionaryProperty)) {
            return null;
        }
        dictionary = dictionary[dictionaryProperty];

        do {
            if (dictionary?.type === EventTypes.CONDITIONAL) {
                return this.computeTextForConditionalType(dictionary, eventProperties);
            } else if (dictionary.type && (dictionary.type === EventTypes.LINK || dictionary.type === EventTypes.GENERIC_THREAT)) {
                return dictionary.text;
            } else if (dictionary.type && dictionary.type === EventTypes.RECURSIVE) {
                return this.computeTextForRecursiveType(dictionary, eventProperties);
            } else if (dictionary.type && dictionary.type === EventTypes.MULTIPLE_CONDITIONAL) {
                return this.computeTextForMultipleConditionalType(dictionary, eventProperties);
            } else if (typeof dictionary === 'object') {
                dictionary = this.recursive(eventProperties, eventProperties, dictionary);
            }
        } while (typeof dictionary === 'object');

        if (dictionaryProperty === EventDescriptors.MAIN_LINK && eventProperties.linkExceptionAdded) {
            return this.valuesService.notificationsKeys.viewExceptionBtn;
        }
        return dictionary;
    }

     /**
     * Based on a given dictionary(object) and some given properties of a notification, returns a string coresponfind to a text key
     * for those properties and notification.
     * @param eventDataParameters An object key + value, the keys are used to return the text key from dictionary
     * @param eventProperties An obbject that includes 'eventDataParameters' in his composition at a certain level
     * @param dictionary An object that contains key + value, the values are text keys
     * @returns A text key - string or an object
     */
     private recursive(eventDataParameters, eventProperties , dictionary): any {
        if (eventProperties.device_os && dictionary[eventProperties.device_os]) {
            return dictionary[eventProperties.device_os];
        }

        if (eventProperties.actual_usages) {
            return this.getDescForReferralNotification(eventProperties, dictionary);
        }

        if (Object.keys(dictionary).length === 1) {
            return dictionary.text;
        } else {
            for (const key in dictionary) {
                if (eventDataParameters.hasOwnProperty(key)) {
                    /* Returns the first value that has the key equal to the first matching notificaiton property */
                    return dictionary[key];
                }
            }
            return dictionary.text;
        }
    }

    /**
     * Computes the text for a referral notification
     * @param notificationData The notification data
     * @param dictionary The dictionary that contains the text keys
     * @returns {string} The text key
     */
    private getDescForReferralNotification(notificationData, dictionary): string {
        let text = '';
        if (notificationData.actual_usages === 1 && dictionary[EventDescriptors.TEXT_FIRST]) {
            text = notificationData.max_usages - notificationData.actual_usages === 1 ? dictionary[EventDescriptors.TEXT_FIRST_ONE] : dictionary[EventDescriptors.TEXT_FIRST];
        } else if ( notificationData.actual_usages > 1 && notificationData.actual_usages < notificationData.max_usages && dictionary[EventDescriptors.TEXT_NEXT]) {
            text = notificationData.max_usages - notificationData.actual_usages === 1 ? dictionary[EventDescriptors.TEXT_LAST] : dictionary[EventDescriptors.TEXT_NEXT];
        } else if (notificationData.actual_usages === notificationData.max_usages && dictionary[EventDescriptors.TEXT_CONGRATS]) {
            text = dictionary[EventDescriptors.TEXT_CONGRATS];
        } else {
            text = dictionary[EventDescriptors.TEXT];
        }
        return text;
    }

     /**
     * Computes the text for a conditional type of event
     * @param dictionary The dictionary that contains the text keys
     * @param eventProperties The event properties that help to determine the text key
     * @returns {string} The text key
     */
    private computeTextForConditionalType(dictionary, eventProperties): string {
        const condition = eventProperties[dictionary[EventDescriptors.CONDITION]];
        if (!dictionary[condition]) {
            return 'notifications.malicious_url.malicious_url.desc.malware';
        }
        return dictionary[condition];
    }

    /**
     * Computes the text for a recursive type of event
     * @param dictionary The dictionary that contains the text keys
     * @param eventProperties The event properties that help to determine the text key
     * @returns {string} The text key
     */
    private computeTextForRecursiveType(dictionary, eventProperties): string {
        let text = '';
        const conditions = dictionary.condition;
        for (const key of conditions) {
            const eventProperty = eventProperties?.[key];
            dictionary = dictionary?.[eventProperty] ?? dictionary;
            if (dictionary && typeof dictionary === 'string') {
                text = dictionary;
                break;
            }
        }
        return typeof text === 'string' ? text : '';
    }

    /**
     * Computes the texts for a multiple conditional type of event
     * @param dictionary The dictionary that contains the text keys
     * @param eventProperties The event properties that help to determine the text key
     * @returns {MultipleConditionalTextElement[]} An array of objects that contain the text key and the placeholders
     */
    private computeTextForMultipleConditionalType(dictionary, eventProperties): MultipleConditionalTextElement[] {
        const condition = dictionary?.condition ?? '';
        let finalStringsArray = [];
        const eventConditionValues = eventProperties?.[condition] ?? [];
        for (const eventConditionValue of eventConditionValues) {
            if (Array.isArray(eventConditionValue)) {
                for (const eventConditionSubvalue of eventConditionValue) {
                    let eventText = this.recursive(eventConditionSubvalue, eventProperties, dictionary);
                    while (typeof eventText === 'object') {
                        eventText = this.recursive(eventConditionSubvalue, eventProperties, eventText);
                    }
                    finalStringsArray.push({text: eventText, placeholders: eventConditionSubvalue});
                }
            } else {
                finalStringsArray.push({text: this.recursive(eventConditionValue, eventProperties, dictionary), placeholders: eventConditionValue});
            }
        }
        return finalStringsArray;
    }
}